<template>
  <!--
    With experiment code:
     - A: is default, is the "control": do nothing, no overlay, as the overlay is the variant under experiment.
     - B: is the experimental variant and thus not the default. Only shown if the user was selected for the experiment
          and assigned to the B variant. Shows the overlay.
    Without any experiment code set (no experiments involved):
     - A: does nothing, as the ABTestWrapper will always default to B.
     - B: is the default as we always want to show the overlay.
  -->
  <MoleculeABTestWrapper v-if="data" :experiment-code="experimentCode" :default-variant="experimentCode ? 'A' : 'B'">
    <template #A></template>
    <template #B>
      <section class="page-overlay-container">
        <div class="page-overlay-wrapper">
          <MoleculeOverlayPanel
            v-if="isPanelOpen && !isPanelDismissed"
            :items="data.content"
            :title="data.title"
            @close="dismissPanel"
            @hide="hidePanel"
            @newsletter-subscribed="dismissPanel(true, true)"
          />
          <MoleculeOverlayStickyButton
            v-if="!isPanelDismissed"
            :data="data"
            class="absolute bottom-14 md:bottom-0 left-0 m-4 pointer-events-auto"
            :open="openPanel"
            :dismiss="dismissPanel"
          />
        </div>
      </section>
    </template>
  </MoleculeABTestWrapper>
</template>

<script setup lang="ts">
const overlayStore = useOverlayStore()
const tixStore = useTixStore()

const { data } = await useAsyncData('page-overlay', () => overlayStore.fetchOverlay())

const cookieKey = computed(() => data.value?.settings?.cookieKey || String(data.value?.id ?? ''))
const experimentCode = computed(() => String(data.value?.settings?.test?.code || ''))

const isPanelOpen = computed(() => overlayStore.isOpen)
const isPanelDismissed = computed(() => overlayStore.isDismissed)

function openPanel() {
  overlayStore.openPanel()
}
function hidePanel() {
  overlayStore.hidePanel()
}
/** What the `_overlay_*` cookie stores: that the visitor closed the panel, and when. */
type OverlayDismissal = { isPanelDismissed: boolean; date: string }

function dismissPanel(doSetCookie = true, subscribed = false) {
  overlayStore.dismissPanel()
  if (doSetCookie && data.value) {
    const hours = subscribed ? 9999 : data.value.settings?.cookieExpiryDuration
    const expiryDate = new Date()
    expiryDate.setTime(expiryDate.getTime() + hours * 60 * 60 * 1000)
    const overlayCookie = useCookie<OverlayDismissal | undefined>(`_overlay_${cookieKey.value}`, { expires: expiryDate, sameSite: 'lax' })
    overlayCookie.value = { isPanelDismissed: true, date: new Date().toISOString() }
  }
}

// Restore a previous dismissal from its cookie. Client-only: the cookie name depends on the fetched
// data, so it cannot be resolved during SSR setup before the fetch resolves.
onMounted(() => {
  if (!data.value) {
    return
  }
  const overlayCookie = useCookie<OverlayDismissal | undefined>(`_overlay_${cookieKey.value}`)
  if (overlayCookie.value?.isPanelDismissed) {
    dismissPanel(false)
  }
})

// Dismiss the overlay once a Tix user is known (logged in).
watch(
  () => tixStore.user,
  (user) => {
    if (user && data.value) {
      dismissPanel()
    }
  },
)
</script>

<style scoped lang="scss">
.page-overlay-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100dvh;
  z-index: 99999;
  pointer-events: none;

  & > .page-overlay-wrapper {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
}
</style>
